/**
 * Phase 6a.18b — source-aware per-contract detail link helper.
 *
 * Every contractId reference rendered in the UI (Sözleşmeler grid,
 * Yevmiye JE card placeholder, WarningsBanner affected-contracts list)
 * navigates to the Phase 6a.18 per-contract detail page through this
 * helper. Keeping a single source of truth means:
 *
 *   • URI encoding is applied uniformly to both path segments — no
 *     surface gets to forget it.
 *   • The Phase 6a.18b1 `/results/` path correction is enforced
 *     centrally; callers can't accidentally regress back to the
 *     edit-route-colliding shape.
 *   • The `?tab=` query parameter is omitted when the requested tab is
 *     "summary" (the page default), keeping shareable URLs clean.
 */

export type ContractDetailTab =
  | "summary"
  | "amortization"
  | "journal"
  | "reconciliation"
  | "metadata"
  | "events";

/**
 * The full set of valid inner-tab keys ContractDetailPage understands
 * via its `?tab=` query parameter. Anything else falls back to
 * `summary`. Phase 6a.20e — `reconciliation` is whitelisted so a deep
 * link `?tab=reconciliation` resolves to the Mutabakat tab when the
 * contract actually has a reconciliation result (the tab itself is
 * still conditionally rendered).
 */
export const CONTRACT_DETAIL_TABS: ContractDetailTab[] = [
  "summary",
  "amortization",
  "journal",
  "reconciliation",
  "metadata",
  "events",
];

/**
 * Build the canonical per-contract detail URL.
 *
 * @param engagementId  Plain (unencoded) engagement id.
 * @param contractId    Plain (unencoded) contract id, possibly containing
 *                      characters that need URL escaping (e.g. `C/A:1`).
 * @param defaultTab    Optional source-aware inner-tab key. When omitted
 *                      or equal to `"summary"` the URL stays clean (no
 *                      query string).
 */
export function buildContractDetailPath(
  engagementId: string,
  contractId: string,
  defaultTab?: ContractDetailTab,
): string {
  const base = `/engagements/${encodeURIComponent(engagementId)}/results/contracts/${encodeURIComponent(contractId)}`;
  if (!defaultTab || defaultTab === "summary") return base;
  return `${base}?tab=${defaultTab}`;
}

/**
 * Phase 6a.18b1.1 — reverse of `buildContractDetailPath`. Given the
 * inner-tab key the per-contract page is currently surfacing (resolved
 * from `?tab=` via `resolveInitialTab`), returns the engagement-level
 * tab route the breadcrumb middle link should navigate back to.
 *
 * Mapping rationale:
 *   • `journal`     → `/journal-entries`  (direct match — engagement-level
 *                                          Yevmiye tab is the natural source)
 *   • `amortization`→ `/movement`          (no engagement-level Amortizasyon
 *                                          tab — Hareket tab is the closest
 *                                          semantic source)
 *   • `summary` / `metadata` / `events`
 *                   → `/contracts`         (these inner tabs have no
 *                                          dedicated engagement-level
 *                                          surface; Sözleşmeler tab is the
 *                                          only source path back)
 */
export function getSourceTabPath(
  engagementId: string,
  sourceTab: ContractDetailTab,
): string {
  const eid = encodeURIComponent(engagementId);
  switch (sourceTab) {
    case "journal":
      return `/engagements/${eid}/results/journal-entries`;
    case "amortization":
      return `/engagements/${eid}/results/movement`;
    case "summary":
    case "metadata":
    case "events":
    default:
      return `/engagements/${eid}/results/contracts`;
  }
}
